/**
 * @file StatementIntegrityModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-19
 * @description One statement's integrity check, opened from its "Xero check"
 *              badge in the approval table. Four figures side by side — what
 *              the statement says, what this system's data says now, what Xero
 *              says under the statement's own rules, and Xero strictly as at
 *              the period end — then every reason they differ, document by
 *              document, and the check's history.
 *
 *              Footer: Close · Re-check (always includes Xero's own aged
 *              report) · Regenerate, or "Approve {n} and regenerate" when the
 *              statement left off period documents nobody had approved yet ·
 *              History (the statement's audit log).
 *
 *              GET  /api/statements/:id/integrity?history=1
 *              POST /api/statements/:id/integrity-check
 *              POST /api/invoices/approve/:id   (existing — approve each left-off document)
 *              POST /api/statements/:id/regenerate
 *
 *              1.1.0 (2026-10-19): on today's API — "Not ready" before the
 *              migration, the regenerate answer's `statement`, an approval
 *              the Xero check took off, and "After a sync" checks.
 *              1.2.0 (2026-10-19): a statement right as at its period end that
 *              changed since (credits allocated after it) — amber, the
 *              "now" figures warn rather than fail, and a line saying
 *              regenerating is optional; the regenerate confirmation says so.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import LoadingSpinner from './LoadingSpinner';
import EntityLink from './EntityLink';
import StatementIntegrityBadge from './StatementIntegrityBadge';
import { useSlideOutButton } from './SlideOutButton';
import './StatementIntegrityModal.css';
import {
  normaliseCheck,
  groupReasons,
  money,
  formatYmdLong,
  formatLondonDateTime,
  docEntityType,
  REASON_SEVERITY_LABELS,
  changedSinceOf,
  changedSinceNote,
  EXCLUDED_REASON_TEXT,
  INTEGRITY_STATUS_LABELS,
  effectiveIntegrityStatus
} from '../utils/statementIntegrity';

const pence = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Math.round(Number(v) * 100));
const differs = (a, b) => {
  const pa = pence(a);
  const pb = pence(b);
  return pa !== null && pb !== null && Math.abs(pa - pb) > 1;
};

const VERDICT_TEXT = { match: '—', penny: 'A penny either way', differs: 'Differs from Xero' };

const ORIGIN_LABELS = { generation: 'After generation', run: 'Re-check all', single: 'Re-check', regenerate: 'After regenerating', sync: 'After a sync' };
const CAUSE_LABELS = {
  changed_since_generation: 'Data changed after generation',
  generation: 'Possible generation fault'
};

function DocLink({ type, id, children }) {
  const entity = docEntityType(type) || 'invoice';
  if (!id) return <span>{children}</span>;
  return <EntityLink type={entity} id={id}>{children}</EntityLink>;
}

function FigureCard({ label, value, note, tone }) {
  return (
    <div className={`stmt-integrity-figure${tone ? ` ${tone}` : ''}`}>
      <span className="stmt-integrity-figure-label">{label}</span>
      <span className="stmt-integrity-figure-value">{value === null || value === undefined ? '—' : money(value)}</span>
      {note && <span className="stmt-integrity-figure-note">{note}</span>}
    </div>
  );
}

// A reason's own message carries its amounts, already worded by the server.
// Its numeric fields are the rules module's integer pence, so they are not
// re-displayed here; money on this screen comes from `detail` (pounds).
function ReasonItem({ reason, periodEndLabel }) {
  const r = reason;
  let direction = null;
  if (r.code === 'X_ASAT_LATER_CREDIT' && r.direction) {
    const word = r.direction === 'overstates' ? 'higher' : 'lower';
    direction = `Statement ${word} than Xero’s ${periodEndLabel || 'period-end'} figure`;
  }
  return (
    <li className={`stmt-integrity-reason ${r.severity || 'info'}`}>
      <div className="stmt-integrity-reason-head">
        {r.docNo && <DocLink type={r.docType} id={r.docId}>{r.docNo}</DocLink>}
        {r.label && !r.docNo && <span className="stmt-integrity-reason-label">{r.label}</span>}
        {r.cause && <span className="stmt-integrity-tag">{CAUSE_LABELS[r.cause] || r.cause}</span>}
        {r.layer && <span className="stmt-integrity-tag muted">{r.layer === 'xero' ? 'vs Xero' : 'vs this system'}</span>}
        {direction && <span className="stmt-integrity-tag warn">{direction}</span>}
      </div>
      <div className="stmt-integrity-reason-msg">{r.message || r.code}</div>
    </li>
  );
}

function StatementIntegrityBody({ loading, loadError, data, row, periodEnd, busy, message, onToggleDocs, showAllDocs, showRows, onToggleRows }) {
  useSlideOutButton([busy, loading, data?.latest?.status, (data?.latest?.excludedUnchecked || []).length]);

  const periodEndLabel = formatYmdLong(periodEnd || data?.latest?.detail?.periodEnd);

  if (loading && !data) return <LoadingSpinner message="Loading the check…" />;
  if (loadError) {
    return (
      <div className="stmt-integrity-line bad">
        <span className="material-symbols-rounded">error</span>
        <span>{loadError}</span>
      </div>
    );
  }
  if (data && data.available === false) {
    return (
      <div className="stmt-integrity-line muted">
        <span className="material-symbols-rounded">database</span>
        <span>{data.reason || 'Not ready — migration statement_reconciliation.sql has not run'}. Statements are not checked against Xero until it has.</span>
      </div>
    );
  }

  const latest = data?.latest || null;
  const history = data?.history || [];
  const detail = latest?.detail || {};
  const status = effectiveIntegrityStatus(latest);
  // Right as at the period end, changed since: amber, and regenerating is optional.
  const changedSince = changedSinceOf(latest);
  const nowTone = changedSince ? 'warn' : 'bad';
  const groups = groupReasons(latest?.reasons);
  const docs = Array.isArray(detail.docs) ? detail.docs : [];
  const reasonByDoc = new Map();
  for (const r of latest?.reasons || []) if (r.docId && !reasonByDoc.has(r.docId)) reasonByDoc.set(r.docId, r);
  const interesting = (d) => (d.verdict && d.verdict !== 'match')
    || differs(d.stmtDue, d.xeroDue) || (!d.onStatement && pence(d.xeroDue)) || reasonByDoc.has(d.docId);
  const shownDocs = showAllDocs ? docs : docs.filter(interesting);
  const excluded = Array.isArray(detail.excluded) ? detail.excluded : [];
  const buckets = Array.isArray(detail.buckets) ? detail.buckets : [];
  const periodRows = Array.isArray(detail.periodRows) ? detail.periodRows : [];
  const acknowledged = latest?.acknowledged
    || (latest?.acknowledgedByName || latest?.acknowledged_at
      ? { byName: latest.acknowledgedByName, at: latest.acknowledgedAt || latest.acknowledged_at } : null);

  return (
    <div className="stmt-integrity-body">
      <div className="stmt-integrity-title">
        <span className="stmt-integrity-account">{row?.accountNo}</span>
        <span className="stmt-integrity-company">{row?.account}</span>
        <span className="stmt-integrity-period">Statement to {periodEndLabel || '—'}</span>
      </div>

      {message && (
        <div className={`stmt-integrity-line ${message.tone || 'muted'}`}>
          <span className="material-symbols-rounded">{message.tone === 'bad' ? 'error' : message.tone === 'good' ? 'check_circle' : 'info'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {!latest ? (
        <div className="stmt-integrity-line muted">
          <span className="material-symbols-rounded">help</span>
          <span>Not checked against Xero yet. Re-check to compare it now.</span>
        </div>
      ) : (
        <>
          <div className="stmt-integrity-status">
            <StatementIntegrityBadge integrity={latest} periodEndLabel={periodEndLabel} />
            <span className="stmt-integrity-headline">{latest.headline || INTEGRITY_STATUS_LABELS[status]}</span>
          </div>

          <div className="stmt-integrity-figures">
            <FigureCard label="On the statement" value={latest.figures.stored} />
            <FigureCard
              label="This system now"
              value={latest.figures.local}
              tone={differs(latest.figures.stored, latest.figures.local) ? nowTone : ''}
            />
            <FigureCard
              label="Xero, on the statement’s rules"
              value={latest.figures.xero}
              tone={differs(latest.figures.stored, latest.figures.xero) ? nowTone : ''}
            />
            <FigureCard
              label={`Xero as at ${periodEndLabel || 'period end'}`}
              value={latest.figures.xeroAsAt}
              tone={differs(latest.figures.stored, latest.figures.xeroAsAt) ? 'warn' : ''}
              note={latest.reportConfirmed === true
                ? <><span className="material-symbols-rounded good">check</span> confirmed by Xero’s aged report</>
                : latest.reportConfirmed === false
                  ? <><span className="material-symbols-rounded bad">warning</span> Xero’s aged report disagrees</>
                  : null}
            />
          </div>

          {changedSince && (
            <div className="stmt-integrity-line warn">
              <span className="material-symbols-rounded">history</span>
              <span>{changedSinceNote(changedSince)}</span>
            </div>
          )}

          {acknowledged && (
            <div className="stmt-integrity-line muted">
              <span className="material-symbols-rounded">how_to_reg</span>
              <span>
                Approved with this mismatch acknowledged{acknowledged.byName ? ` by ${acknowledged.byName}` : ''}
                {acknowledged.at ? `, ${formatLondonDateTime(acknowledged.at)}` : ''}.
              </span>
            </div>
          )}
          {latest.approvalCleared && (
            <div className="stmt-integrity-line warn">
              <span className="material-symbols-rounded">undo</span>
              <span>The Xero check took this statement’s approval off (it had been approved before this difference was found) — review it, regenerate it if it is out of date, and approve it again once it is right.</span>
            </div>
          )}
          {detail.note && (
            <div className="stmt-integrity-line muted">
              <span className="material-symbols-rounded">info</span>
              <span>{detail.note}</span>
            </div>
          )}

          {groups.length > 0 && (
            <div className="stmt-integrity-section">
              {groups.map((g) => (
                <div key={g.severity} className="stmt-integrity-reason-group">
                  <h5 className={g.severity}>{REASON_SEVERITY_LABELS[g.severity]} ({g.reasons.length})</h5>
                  <ul className="stmt-integrity-reasons">
                    {g.reasons.map((r, i) => <ReasonItem key={`${r.code}-${r.docId || r.label || i}-${i}`} reason={r} periodEndLabel={periodEndLabel} />)}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {docs.length > 0 && (
            <div className="stmt-integrity-section">
              <div className="stmt-integrity-section-head">
                <h5>Documents {showAllDocs ? `(${docs.length})` : `that differ (${shownDocs.length} of ${docs.length})`}</h5>
                <button type="button" className="stmt-integrity-toggle" onClick={onToggleDocs}>
                  {showAllDocs ? 'Show only those that differ' : `Show all ${docs.length}`}
                </button>
              </div>
              {shownDocs.length === 0 ? (
                <div className="stmt-integrity-line muted small">Every document agrees.</div>
              ) : (
                <div className="stmt-integrity-table-wrap">
                  <table className="data-table stmt-integrity-docs">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Date</th>
                        <th>Due</th>
                        <th className="center">On statement</th>
                        <th className="right">Here</th>
                        <th className="right">Xero (rules)</th>
                        <th className="right">Xero as at</th>
                        <th className="right">Difference</th>
                        <th>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownDocs.map((d, i) => {
                        const diff = pence(d.stmtDue) !== null && pence(d.xeroDue) !== null
                          ? (pence(d.stmtDue) - pence(d.xeroDue)) / 100 : null;
                        const r = reasonByDoc.get(d.docId);
                        const why = r?.message || VERDICT_TEXT[d.verdict] || d.verdict || '—';
                        return (
                          <tr key={`${d.docId || d.xeroId || i}`} className={diff !== null && Math.abs(diff) > 0.01 ? 'row-with-issue' : ''}>
                            <td><DocLink type={d.type} id={d.docId}>{d.docNo || d.xeroId || '—'}</DocLink></td>
                            <td>
                              {formatYmdLong(d.date) || '—'}
                              {d.xeroDate && d.xeroDate !== d.date && <span className="stmt-integrity-xero-alt">Xero {formatYmdLong(d.xeroDate)}</span>}
                            </td>
                            <td>
                              {formatYmdLong(d.dueDate) || '—'}
                              {d.xeroDueDate && d.xeroDueDate !== d.dueDate && <span className="stmt-integrity-xero-alt">Xero {formatYmdLong(d.xeroDueDate)}</span>}
                            </td>
                            <td className="center">{d.onStatement ? 'Yes' : 'No'}</td>
                            <td className="right">{money(d.stmtDue)}</td>
                            <td className="right">{money(d.xeroDue)}</td>
                            <td className="right">{money(d.xeroAsAt)}</td>
                            <td className="right">{diff === null ? '—' : money(diff)}</td>
                            <td className="stmt-integrity-why">{why}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {(buckets.length > 0 || periodRows.length > 0) && (
            <div className="stmt-integrity-section">
              <div className="stmt-integrity-section-head">
                <h5>Statement rows</h5>
                <button type="button" className="stmt-integrity-toggle" onClick={onToggleRows}>
                  {showRows ? 'Hide' : `Show ${buckets.length + periodRows.length}`}
                </button>
              </div>
              {showRows && (
                <div className="stmt-integrity-table-wrap">
                  <table className="data-table stmt-integrity-rows">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th className="right">On the statement</th>
                        <th className="right">This system now</th>
                        <th className="right">Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buckets.map((b, i) => (
                        <tr key={`b-${b.label}-${b.kind}-${i}`} className={differs(b.stored, b.local) ? 'row-with-issue' : ''}>
                          <td>{b.label}{b.kind === 'overpayment' ? ' (overpayments)' : ''}</td>
                          <td className="right">{money(b.stored)}</td>
                          <td className="right">{money(b.local)}</td>
                          <td className="right">{pence(b.stored) !== null && pence(b.local) !== null ? money((pence(b.stored) - pence(b.local)) / 100) : '—'}</td>
                        </tr>
                      ))}
                      {periodRows.map((p, i) => (
                        <tr key={`p-${p.docId || p.docNo}-${i}`} className={differs(p.stored, p.local) ? 'row-with-issue' : ''}>
                          <td><DocLink type="invoice" id={p.docId}>{p.docNo || '—'}</DocLink></td>
                          <td className="right">{money(p.stored)}</td>
                          <td className="right">{money(p.local)}</td>
                          <td className="right">{pence(p.stored) !== null && pence(p.local) !== null ? money((pence(p.stored) - pence(p.local)) / 100) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {excluded.length > 0 && (
            <div className="stmt-integrity-section">
              <h5>Left off this statement ({excluded.length})</h5>
              <ul className="stmt-integrity-excluded">
                {excluded.map((e, i) => (
                  <li key={`${e.docId || e.docNo}-${i}`}>
                    <DocLink type={e.type || 'invoice'} id={e.docId}>{e.docNo || '—'}</DocLink>
                    <span className="stmt-integrity-excluded-reason">
                      {EXCLUDED_REASON_TEXT[e.reason] || e.reason}
                      {e.reason === 'unapproved' && (e.markedByName || e.markedBy
                        ? ` by ${e.markedByName || e.markedBy}` : ' — open it to see who in its history')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {history.length > 0 && (
        <div className="stmt-integrity-section">
          <h5>Check history</h5>
          <div className="stmt-integrity-table-wrap">
            <table className="data-table stmt-integrity-history">
              <thead>
                <tr>
                  <th>Checked</th>
                  <th>Result</th>
                  <th>How</th>
                  <th>By</th>
                  <th className="right">Statement</th>
                  <th className="right">Here</th>
                  <th className="right">Xero (rules)</th>
                  <th className="right">Xero as at</th>
                  <th className="center">Aged report</th>
                  <th>Acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h0, i) => {
                  const h = normaliseCheck(h0);
                  return (
                    <tr key={h.id || i}>
                      <td>{formatLondonDateTime(h.checkedAt) || '—'}</td>
                      <td>{INTEGRITY_STATUS_LABELS[h.status] || h.status}</td>
                      <td>{ORIGIN_LABELS[h.origin] || h.origin || '—'}</td>
                      <td>{h.checkedByName || '—'}</td>
                      <td className="right">{money(h.figures.stored)}</td>
                      <td className="right">{money(h.figures.local)}</td>
                      <td className="right">{money(h.figures.xero)}</td>
                      <td className="right">{money(h.figures.xeroAsAt)}</td>
                      <td className="center">{h.reportConfirmed === true ? 'Agrees' : h.reportConfirmed === false ? 'Disagrees' : '—'}</td>
                      <td>{h.acknowledgedByName || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {number} statementId
 * @param {object} row            - the statement's approval-table row (for the title, delivery state, excludedUnchecked)
 * @param {string} periodEnd      - 'YYYY-MM-DD'
 * @param {boolean} canCheck      - holds statements.generate and is not read-only
 * @param {boolean} integrityAvailable
 * @param {boolean} runCheckActive - the run's own check job is still producing verdicts
 * @param {function} onClose      - the parent refreshes its list here
 * @param {function} onChanged    - (payload) after a re-check or regenerate, so the row can update at once
 */
function StatementIntegrityModal({ statementId, row, periodEnd, canCheck = false, integrityAvailable = true, runCheckActive = false, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null); // 'recheck' | 'regenerate' | null
  const [message, setMessage] = useState(null);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [showRows, setShowRows] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    if (!statementId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_URL}/api/statements/${statementId}/integrity?history=1`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Could not load the check (HTTP ${res.status})`);
      setData({
        available: body.available !== false,
        reason: body.reason || null,
        latest: body.latest ? normaliseCheck(body.latest) : null,
        history: Array.isArray(body.history) ? body.history : []
      });
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statementId]);

  useEffect(() => { load(); }, [load]);

  const latest = data?.latest || null;
  const excludedUnchecked = (() => {
    const fromDetail = (latest?.detail?.excluded || []).filter((e) => e.reason === 'unchecked' && e.docId);
    if (fromDetail.length) return fromDetail.map((e) => ({ docId: e.docId, docNo: e.docNo }));
    return latest?.excludedUnchecked || row?.integrity?.excludedUnchecked || [];
  })();
  const delivered = !!row?.delivered_at || (row?.status && row.status !== 'generated');
  const available = integrityAvailable && data?.available !== false;

  const recheckDisabledReason = !canCheck ? 'You need permission to generate statements'
    : !available ? (data?.reason || 'Not ready — migration statement_reconciliation.sql has not run')
      : runCheckActive ? 'Wait for the run’s Xero check to finish'
        : busy ? 'Working…' : '';
  const regenDisabledReason = !canCheck ? 'You need permission to generate statements'
    : delivered ? 'This statement has already been sent — it can’t be regenerated'
      : runCheckActive ? 'Wait for the run’s Xero check to finish'
        : busy ? 'Working…' : '';

  const recheck = async () => {
    setBusy('recheck');
    setMessage({ tone: 'muted', text: 'Comparing with this system’s data and with Xero (including Xero’s own aged report)…' });
    try {
      const res = await fetch(`${API_URL}/api/statements/${statementId}/integrity-check`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body.code === 'CHECK_IN_PROGRESS') {
        setMessage({ tone: 'warn', text: 'The run’s Xero check is still running — wait for it to finish, then re-check.' });
        return;
      }
      if (!res.ok) throw new Error(body.error || `The check failed (HTTP ${res.status})`);
      onChanged?.(body);
      await load();
      setMessage({ tone: 'good', text: 'Checked again just now.' });
    } catch (e) {
      setMessage({ tone: 'bad', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    setBusy('regenerate');
    try {
      // Approve each left-off period document first, through the invoice
      // approval route (which writes its own audit row). Stop at the first
      // failure and say which one, rather than regenerating a half-approved set.
      for (let i = 0; i < excludedUnchecked.length; i += 1) {
        const doc = excludedUnchecked[i];
        setMessage({ tone: 'muted', text: `Approving ${doc.docNo || `document ${doc.docId}`} (${i + 1} of ${excludedUnchecked.length})…` });
        const res = await fetch(`${API_URL}/api/invoices/approve/${doc.docId}`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'approved' })
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(`Could not approve ${doc.docNo || doc.docId}: ${body.error || `HTTP ${res.status}`} — the statement was not regenerated`);
        }
      }
      setMessage({ tone: 'muted', text: 'Regenerating the statement and checking it…' });
      const res = await fetch(`${API_URL}/api/statements/${statementId}/regenerate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body.code === 'ALREADY_DELIVERED') {
        throw new Error('This statement has already been sent, so it can’t be regenerated.');
      }
      if (!res.ok) throw new Error(body.error || `Regenerate failed (HTTP ${res.status})`);
      onChanged?.(body);
      await load();
      setMessage(body.statement?.needsStatement === false
        ? { tone: 'warn', text: 'Regenerated — on today’s data this account would not need a statement at all. Its approval is reset; decide whether to send it.' }
        : { tone: 'good', text: 'Regenerated from the current data and checked. Its approval is reset to unchecked.' });
    } catch (e) {
      setMessage({ tone: 'bad', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const askRegenerate = () => {
    if (excludedUnchecked.length > 0) {
      setConfirm({
        title: `Approve ${excludedUnchecked.length} and regenerate?`,
        message: (
          <div className="stmt-integrity-confirm">
            <p>These period documents were left off the statement because nobody had approved them for statements yet. They will be approved, then the statement rebuilt from the current data:</p>
            <ul>{excludedUnchecked.map((d) => <li key={d.docId}>{d.docNo || d.docId}</li>)}</ul>
            <p>The statement’s approval will be reset to unchecked.</p>
          </div>
        ),
        confirmText: 'Approve and regenerate',
        onConfirm: regenerate
      });
    } else {
      setConfirm({
        title: 'Regenerate this statement?',
        message: changedSinceOf(latest)
          ? 'This statement was right as at its period end, so regenerating is optional: it will be rebuilt to show the latest (the credits allocated since) and checked again. Its approval will be reset to unchecked.'
          : 'The statement will be rebuilt from the current data and checked again. Its approval will be reset to unchecked.',
        confirmText: 'Regenerate',
        onConfirm: regenerate
      });
    }
  };

  const regenLabel = busy === 'regenerate' ? 'Regenerating…'
    : excludedUnchecked.length > 0 ? `Approve ${excludedUnchecked.length} and regenerate` : 'Regenerate';

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Statement check against Xero"
        size="xl"
        className="statement-integrity"
        footer={
          <div className="buttons slideout stmt-integrity-footer">
            <div>
              <button type="button" className="btn-slideout" data-read-safe onClick={() => setShowHistory(true)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">history</span></div>
                <span className="btn-label">History</span>
              </button>
            </div>
            <div>
              <button
                type="button"
                className="btn-slideout"
                data-write-action
                onClick={askRegenerate}
                disabled={!!regenDisabledReason}
                title={regenDisabledReason}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">{excludedUnchecked.length > 0 ? 'task_alt' : 'refresh'}</span></div>
                <span className="btn-label">{regenLabel}</span>
              </button>
            </div>
            <div>
              <button
                type="button"
                className="btn-slideout"
                data-write-action
                onClick={recheck}
                disabled={!!recheckDisabledReason}
                title={recheckDisabledReason || 'Compare again with this system and with Xero, including Xero’s own aged report'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">fact_check</span></div>
                <span className="btn-label">{busy === 'recheck' ? 'Checking…' : 'Re-check'}</span>
              </button>
            </div>
            <div>
              <button type="button" className="btn-slideout" data-read-safe onClick={onClose}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Close</span>
              </button>
            </div>
          </div>
        }
      >
        <StatementIntegrityBody
          loading={loading}
          loadError={loadError}
          data={data}
          row={row}
          periodEnd={periodEnd}
          busy={busy}
          message={message}
          showAllDocs={showAllDocs}
          onToggleDocs={() => setShowAllDocs((v) => !v)}
          showRows={showRows}
          onToggleRows={() => setShowRows((v) => !v)}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirm?.title}
        type="warning"
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        cancelText="Cancel"
        onConfirm={() => { const fn = confirm?.onConfirm; setConfirm(null); if (fn) fn(); }}
        onCancel={() => setConfirm(null)}
      />

      {showHistory && (
        <AuditHistoryModal
          isOpen={true}
          onClose={() => setShowHistory(false)}
          entityType="statement"
          entityId={statementId}
          entityIdentifier={`Statement ${row?.accountNo || statementId}`}
        />
      )}
    </>
  );
}

export default StatementIntegrityModal;
